/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `directoryInit` for the Caintra Connect frontend.
 * @module logic/directory/directoryInit
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { getReferenceData } from "../companyEdit";
import { getSectors } from "../requirements";
import { getUserRoleData } from "../user";
import {
  browseCompaniesFromOpensearch,
  preloadAllCompanies,
} from "./index.js";
import {
  clearDirectoryListSession,
  isValidDirectoryRestoreSession,
  readDirectoryListSession,
} from "../../utils/directory/directoryListSession.js";
import { DEFAULT_RESULTS_LIMIT } from "../../utils/resultsLimitOptions.js";

export const EMPTY_DIRECTORY_FILTERS = {
  sector: null,
  subSector: null,
  rama: null,
  tamanoDeLaEmpresa: null,
  estado: null,
  municipio: null,
  exporta: null,
  importa: null,
};

export const loadDirectoryReferenceData = async (navigate, userId) => {
  const [rolesData, sectorsData, statesData, townsData] = await Promise.all([
    getUserRoleData(userId, navigate),
    getSectors(navigate),
    getReferenceData("states", navigate),
    getReferenceData("towns", navigate),
  ]);

  return {
    rolesData,
    sectorsData,
    statesData: statesData || [],
    townsData: townsData || [],
  };
};

const preloadCompaniesFromCursor = (
  navigate,
  lastSort,
  initialItems,
  syncBrowseCompanies,
  setLastSort,
  setLimitReached,
) => {
  preloadAllCompanies(
    navigate,
    lastSort,
    initialItems,
    syncBrowseCompanies,
    setLastSort,
    setLimitReached,
  );
};

export const buildDirectorySessionRestoreState = (saved) => ({
  searchQuery: saved.searchQuery ?? "",
  filters: saved.filters ?? EMPTY_DIRECTORY_FILTERS,
  lastSort: saved.lastSort ?? null,
  initialLastSort: saved.initialLastSort ?? null,
  filteredCompanyData: Array.isArray(saved.filteredCompanyData)
    ? saved.filteredCompanyData
    : [],
  originalCompaniesData: Array.isArray(saved.originalCompaniesData)
    ? saved.originalCompaniesData
    : [],
  currentPage:
    typeof saved.currentPage === "number" && saved.currentPage >= 1
      ? saved.currentPage
      : 1,
  resultsLimit:
    typeof saved.resultsLimit === "number"
      ? saved.resultsLimit
      : DEFAULT_RESULTS_LIMIT,
  totalResults:
    typeof saved.totalResults === "number" ? saved.totalResults : 0,
  limitReached: Boolean(saved.limitReached),
  subSectors: Array.isArray(saved.subSectors) ? saved.subSectors : [],
  branches: Array.isArray(saved.branches) ? saved.branches : [],
});

export const restoreDirectoryBrowseState = async (navigate, saved, setters) => {
  const restoreState = buildDirectorySessionRestoreState(saved);
  setters.applyRestoreState(restoreState);

  if (
    restoreState.filteredCompanyData.length > 0 &&
    restoreState.lastSort &&
    !restoreState.limitReached
  ) {
    preloadCompaniesFromCursor(
      navigate,
      restoreState.lastSort,
      restoreState.filteredCompanyData,
      setters.syncBrowseCompanies,
      setters.setLastSort,
      setters.setLimitReached,
    );
  }
};

export const loadInitialDirectoryCompanies = async (navigate, setters) => {
  logger.info("Browsing companies...");
  const companiesData = await browseCompaniesFromOpensearch(navigate, null);
  const firstPageItems = Array.isArray(companiesData?.items)
    ? companiesData.items
    : [];

  setters.setTotalResults(companiesData?.total ?? 0);
  setters.syncBrowseCompanies(firstPageItems);
  setters.setLastSort(companiesData?.lastSort ?? null);
  setters.setInitialLastSort(companiesData?.lastSort ?? null);
  setters.setLimitReached(!companiesData?.lastSort);

  if (companiesData?.lastSort) {
    preloadCompaniesFromCursor(
      navigate,
      companiesData.lastSort,
      firstPageItems,
      setters.syncBrowseCompanies,
      setters.setLastSort,
      setters.setLimitReached,
    );
  }
};

/**
 * Initializes directory reference data and company list (restore or browse).
 */
export const initializeDirectoryPage = async ({
  navigate,
  userId,
  changePermissions,
  setters,
}) => {
  logger.info("Fetching reference data...");
  const referenceData = await loadDirectoryReferenceData(navigate, userId);

  if (referenceData.rolesData != null) {
    changePermissions(referenceData.rolesData);
  }

  setters.setSectors(referenceData.sectorsData);
  setters.setTowns(referenceData.townsData);
  setters.setGeneralTowns(referenceData.townsData);
  setters.setStates(referenceData.statesData);

  const saved = readDirectoryListSession();
  if (saved?.awaitingRestore && !isValidDirectoryRestoreSession(saved)) {
    logger.info("Directory list state is not valid, clearing...");
    clearDirectoryListSession();
  }

  if (isValidDirectoryRestoreSession(saved)) {
    await restoreDirectoryBrowseState(navigate, saved, setters);
    return;
  }

  await loadInitialDirectoryCompanies(navigate, setters);
};

export const resetDirectoryReferenceData = (setters) => {
  setters.setOriginalCompaniesData([]);
  setters.setFilteredCompanyData([]);
  setters.setTowns([]);
  setters.setGeneralTowns([]);
  setters.setStates([]);
  setters.setSectors([]);
};

export const logDirectoryInitError = (error) => {
  logger.error("Error fetching reference data:", {
    err: sanitizeError(error),
  });
};
